import { useTheme } from '@mui/material';
import { useGiftContext } from '../Context/GiftContextProvider';
import { useCallback, useRef, useState } from 'react';
import useUploadGiftSenderImage from './useUploadGiftSenderImage';
import { Dayjs } from 'dayjs';
import {
  DEFAULT_PHOTO,
  MAX_GREETING_LENGTH,
  MAX_GREETING_ROWS,
  MAX_SENDER_NAME_LENGTH,
} from '../consts';
import { useAlertContext } from '../../../contexts/AlertContext';
import { AlertType } from '../../../design-system/DSAlert';
import { isCustomPendulumLogo } from '../utils';

const useGiftForm = () => {
  const theme = useTheme();
  const {
    setKlydoGiftProps,
    klydoGiftProps,
    resetSenderImageUrl,
    giftCategories,
  } = useGiftContext();
  // A custom-logo greet is drawn with no sender name, so the field is hidden.
  const showSenderName = !isCustomPendulumLogo(
    klydoGiftProps?.pendulumLogoUrl,
    giftCategories,
  );
  const { showAlert } = useAlertContext();
  const [greeting, setGreeting] = useState(
    klydoGiftProps?.greetingContent || '',
  );
  const [name, setName] = useState(klydoGiftProps?.senderName || '');
  const [profileUrl, setProfileUrl] = useState(
    klydoGiftProps?.senderImageUrl || DEFAULT_PHOTO,
  );
  const [generateGreetDialogOpen, setGenerateGreetDialogOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { handleFileSelection: handleProfileUpload, isPending: isUploading } =
    useUploadGiftSenderImage({
      setUrl: setProfileUrl,
      onSuccess: (url) => setKlydoGiftProps({ senderImageUrl: url }),
      onError: () => {
        setProfileUrl(DEFAULT_PHOTO);
        resetSenderImageUrl();
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
    });

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleGreetChange = (greet: string) => {
    if (greet.length > MAX_GREETING_LENGTH) {
      showAlert({
        alertType: AlertType.ERROR,
        message: `Greeting should be no more than ${MAX_GREETING_LENGTH} characters`,
      });
      return;
    }
    if (greet.split('\n').length > MAX_GREETING_ROWS) {
      showAlert({
        alertType: AlertType.ERROR,
        message: `Greeting should not be more than ${MAX_GREETING_ROWS} rows`,
      });
      return;
    }
    setGreeting(greet);
    setKlydoGiftProps({ greetingContent: greet });
  };

  const handleNameChange = (senderName: string) => {
    if (senderName.length > MAX_SENDER_NAME_LENGTH) return;
    setName(senderName);
    setKlydoGiftProps({ senderName });
  };

  const handleDateChange = (selectedDate: Dayjs | null) => {
    if (!selectedDate?.isValid()) {
      setKlydoGiftProps({ date: undefined });
      return;
    }
    setKlydoGiftProps({ date: selectedDate.valueOf() });
  };

  const handleRemoveProfile = () => {
    setProfileUrl(DEFAULT_PHOTO);
    resetSenderImageUrl();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const linesLimit = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key !== 'Enter') return;
      const textarea = event.target as HTMLTextAreaElement;
      const { selectionStart, selectionEnd, value } = textarea;
      const resultText =
        value.slice(0, selectionStart) + '\n' + value.slice(selectionEnd);
      if (resultText.split('\n').length > MAX_GREETING_ROWS) {
        event.preventDefault();
        showAlert({
          alertType: AlertType.ERROR,
          message: `Greeting should not be more than ${MAX_GREETING_ROWS} rows`,
        });
      }
    },
    [],
  );

  const handleGreetPaste = useCallback((event: React.ClipboardEvent) => {
    const pastedText = event.clipboardData.getData('text');
    const textarea = event.target as HTMLTextAreaElement;
    const { selectionStart, selectionEnd, value } = textarea;
    const resultText =
      value.slice(0, selectionStart) + pastedText + value.slice(selectionEnd);
    if (resultText.split('\n').length > MAX_GREETING_ROWS) {
      event.preventDefault();
      showAlert({
        alertType: AlertType.ERROR,
        message: `Greeting should not be more than ${MAX_GREETING_ROWS} rows`,
      });
      return;
    }
    if (resultText.length > MAX_GREETING_LENGTH) {
      event.preventDefault();
      showAlert({
        alertType: AlertType.ERROR,
        message: `Greeting should be no more than ${MAX_GREETING_LENGTH} characters`,
      });
    }
  }, []);

  const handleSaveGenerateGreet = useCallback(
    (greet: string) => {
      handleGreetChange(greet);
      setGenerateGreetDialogOpen(false);
    },
    [greeting],
  );

  return {
    theme,
    greeting,
    name,
    showSenderName,
    profileUrl,
    fileInputRef,
    klydoGiftProps,
    defaultPhoto: DEFAULT_PHOTO,
    handleAvatarClick,
    handleGreetChange,
    handleNameChange,
    handleDateChange,
    handleRemoveProfile,
    isUploading,
    handlePickProfile: handleProfileUpload,
    linesLimit,
    handleGreetPaste,
    handleClickGenerateGreet: () => setGenerateGreetDialogOpen(true),
    generateGreetDialogOpen,
    handleCloseGenerateGreetDialog: () => setGenerateGreetDialogOpen(false),
    handleSaveGenerateGreet,
  };
};

export default useGiftForm;
